"use client";

import React, { useEffect, useRef, useState } from "react";
import { useScroll } from "framer-motion";
import Testimonials from "./Testimonials";
import StatsSection from "./StatusSection";
import { ManagePayments as Icon_ManagePayments } from "@/components/customer/icons/ManagePayments";
import { ProfileCustomization as Icon_ProfileCustomization } from "@/components/customer/icons/ProfileCustomization";
import { RatingFeedBack as Icon_RatingFeedBack } from "@/components/customer/icons/RatingFeedBack";
import { TicketBookings as Icon_TicketBookings } from "@/components/customer/icons/TicketBookings";
const SvgIcons = { ManagePayments: Icon_ManagePayments, ProfileCustomization: Icon_ProfileCustomization, RatingFeedBack: Icon_RatingFeedBack, TicketBookings: Icon_TicketBookings };
import Image from "next/image";
import FaqSection from "./FaqSection";
import ProfileCardDashboard from "./ProfileCardDashboard";
import { getImageUrl } from "@/lib/imageUrl";
import CircularTestimonialView from "./CircularTestimonialView";

// Define the props interface for FaqItem
interface FaqItemProps {
  key: number;
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  isLast: boolean;
}

// FaqItem Component
const FaqItem: React.FC<FaqItemProps> = ({ question, answer, isOpen, onToggle, isLast }) => {
  return (
    <div
      className={`py-[22px] w-full items-start self-stretch 
        border-b-[1px] border-[#D8E9FF]
      `}
    >
      <div>
        <div
          className="flex items-center w-full justify-between cursor-pointer"
          onClick={onToggle}
        >
          <h3 className="text-[#090D28] font-Schibsted_Grotesk font-bold text-[20px] md:text-[22px]  leading-[100%]">
            {question}
          </h3>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          >
            <path
              d="M4.16668 13L10 7.16663L15.8333 13"
              stroke={isOpen ? '#1558BC' : 'black'}
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
            }`}
        >
          <p className="text-[#2D3238] font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] mt-[8px]">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const VilvaOne: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openIndex2, setOpenIndex2] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const faqs = [
    {
      question: "What is Vilva One?",
      answer: "Vilva One is an all-in-one platform that helps businesses launch fully functional bus booking apps and quick commerce (delivery) marketplace apps with powerful admin dashboards."
    },
    {
      question: "What types of apps can I create with Vilva One?",
      answer: "You can build branded transport service apps for bus booking and delivery marketplace apps for quick commerce businesses using Vilva One."
    },
    {
      question: "Who is Vilva One designed for?",
      answer: "Vilva One is suitable for startups, small businesses, and scaling enterprises looking to operate mobility or delivery platforms efficiently."
    },
    {
      question: "How does Vilva One help with operations management?",
      answer: "Vilva One provides real-time dashboards that allow you to track bookings, orders, payments, invoices, and user activity from a single place."
    },
    {
      question: "Can customers book bus tickets using Vilva One apps?",
      answer: "Yes. Users can search for buses, select seats, book instantly, and access e-tickets directly within the Vilva One bus booking app."
    },
    {
      question: "Does Vilva One support delivery tracking?",
      answer: "Yes. The platform includes real-time order tracking for quick commerce deliveries along with order history and e-receipts."
    },
    {
      question: "What payment features does Vilva One offer?",
      answer: "Vilva One supports secure payments and refunds, along with payment history and invoice management for business owners."
    },
    {
      question: "Can businesses customize user profiles?",
      answer: "Yes. Vilva One allows users to manage their profiles, preferences, and notification settings directly within the app."
    },
    {
      question: "Is Vilva One suitable for new startups with no tech team?",
      answer: "Yes. Vilva One is designed so entrepreneurs can deploy and manage apps without deep technical expertise, supported by an easy-to-use admin dashboard."
    },
    {
      question: "How quickly can I go live with an app using Vilva One?",
      answer: "Deployment timelines depend on your business requirements, but Vilva One's toolkit is built to accelerate the launch of apps and admin panels quickly."
    }
  ];

  // FIX: isMobile declared once at root level and passed down as prop
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const faqData = [
    {
      question: 'Real-Time Bus Search & Filters',
      answer: 'Find buses by location, date, operator, and seat availability instantly.',
    },
    {
      question: 'Live Seat Selection & Instant Booking',
      answer: 'Select your preferred seat in real-time and book instantly with ease.',
    },
    {
      question: 'Booking History & E-Ticket Access',
      answer: 'View your booking history and access e-tickets anytime, anywhere.',
    },
    {
      question: 'Secure Payments & Refunds',
      answer: 'Make secure payments and process refunds seamlessly through our platform.',
    },
  ];

  const faqData2 = [
    {
      question: 'Real-Time Delivery Search & Filters',
      answer: 'Search for delivery options by location, date, and availability instantly.',
    },
    {
      question: 'Live Order Tracking & Instant Booking',
      answer: 'Track your orders in real-time and book deliveries with ease.',
    },
    {
      question: 'Order History & E-Receipt Access',
      answer: 'Access your order history and e-receipts anytime, anywhere.',
    },
    {
      question: 'Secure Payments & Refunds',
      answer: 'Make secure payments and process refunds seamlessly through our platform.',
    },
  ];

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleToggle2 = (index: number) => {
    setOpenIndex2(openIndex2 === index ? null : index);
  };

  return (
    <div ref={containerRef} className="overflow-x-hidden">
      {/* Hero Section */}
      <div className="bg-white">
        <section className="bg-[#FFFFFF] bg-opacity-30 py-[60px] lg:py-24">
          <div className="mx-auto container px-4 md:px-7">
            <div className="grid items-center grid-cols-1 md:grid-cols-2 gap-12 lg:grid-cols-2">
              <HeroText />
              {/* FIX: pass isMobile as prop */}
              <HeroImage isMobile={isMobile} />
            </div>
          </div>
        </section>
      </div>

      {/* Dashboard Section */}
      <DashboardSection />

      {/* Bus Booking Section — FIX: pass isMobile as prop */}
      <BusBookingSection faqData={faqData} openIndex={openIndex} handleToggle={handleToggle} isMobile={isMobile} />

      {/* Q-Commerce Section */}
      <QCommerceSection faqData2={faqData2} openIndex2={openIndex2} handleToggle2={handleToggle2} />

      {/* Why Teams Love Us Section */}
      <div className="py-[60px] md:py-[100px] px-4 md:px-0 md:container">
        <h2 className="text-[#090D28] text-center text-[32px] md:text-[48px] leading-[120%] font-NeueMontreal">Why teams love{" "}
          <span className="text-[32px] md:text-[48px]  leading-[120%] font-NeueMontreal bg-gradient-to-r from-[#1586FF] to-[#003FDF] bg-clip-text text-transparent"
          >Working With VilvaOne</span> </h2>
        <p className="text-[#000]/70 text-center text-[16px] md:text-[18px] font-[400] leading-[150%] !font-Schibsted_Grotesk py-4">When our customers win, we win. Here’s how they’ve used our tools to succeed.</p>


        <section className="flex items-center justify-center gap-3">
          <Image
            src="/images/assets/Google-review.svg"
            width={220}
            height={220}
            alt="Google review tag"
            quality={75}
            className=""
            style={{ width: 'auto', height: 'auto' }}
          />
          <Image
            src="/images/assets/Just-dail.svg"
            width={220}
            height={220}
            alt="Justdial review tag"
            quality={75}
            className=""
            style={{ width: 'auto', height: 'auto' }}
          />
        </section>

        {/* <section className="md:container px-[16px] pt-[20px] md:pt-[80px]">
          <Testimonials page="business-website" />
        </section> */}

         <section className="md:container px-[16px] pt-[20px] md:pt-[80px] flex justify-center items-center">
                          <CircularTestimonialView page="vilvad2c" />
                        </section>

      </div>

      {/* FAQ Section */}
      <div className="max-w-2xl mb-[60px] md:mb-[150px] mx-auto text-center">
        <FaqSection faqs={faqs} />
      </div>
    </div>
  );
};

// ─── Extracted Components ─────────────────────────────────────────────────────

const HeroText = () => {
  const ref = useRef(null);

  return (
    <div >
      <p className="text-[18px] md:text-[20px] font-normal tracking-[3.2px] uppercase font-Schibsted_Grotesk bg-gradient-to-r from-[#0023B0] via-[#0066D6] to-[#00ADEC] bg-clip-text text-transparent">
        VilvaOne
      </p>
      <h1 className="text-[var(--Dark-Color,#090D28)] my-4  font-NeueMontreal text-[40px] md:text-[40px] lg:text-[56px] not-italic font-[700] leading-[120%] self-stretch">
        Launch fully functional Apps and Admin Dashboards
      </h1>
      <p className="text-[#373844] mb-6 font-Schibsted_Grotesk text-[18px] lg:text-[20px] not-italic font-normal leading-[150%]">
        VilvaOne gives you the complete toolkit to run both a transport service and a delivery marketplace. Whether you're a startup or scaling enterprise, deploy branded apps, manage operations with powerful dashboards, and grow with confidence
      </p>
      <a
        href="/book-demo"
        title="Book Demo"
        className="text-white font-Schibsted_Grotesk text-[18px] md:text-[20px] not-italic font-normal leading-[120%] flex w-[180px] h-[52px] px-8 py-4 justify-center items-center rounded-[8px] bg-[var(--Main-colour,#195AFF)]"
        role="button"
      >
        Book Demo
      </a>
    </div>
  );
};

// FIX: was a broken syntax — fixed to a proper component that accepts isMobile prop
const HeroImage = ({ isMobile }: { isMobile: boolean }) => {
  const ref = useRef(null);

  return (
    <div>
      <div className="relative px-0 md:px-5 flex items-center justify-center">
        <div className="rounded-[17.605px_17.605px_88.025px_17.605px] relative md:rounded-[35.2px_35.2px_176px_35.2px] bg-gradient-to-b from-[rgba(143,174,255,0)] to-[#EBF0FF] w-fit h-fit shrink-0">
          <span className="flex w-[61.618px] md:w-[116.6px] animate-float3 absolute top-20 -left-2 md:-left-20 h-[18.155px] md:h-[36.3px] px-[17.6px] py-[8.8px] whitespace-nowrap justify-center items-center shrink-0 rounded-[4px] md:rounded-[8.8px] border border-[#0ABC19] bg-[#EFFFF0] text-[#008D0C] font-LatinkaSemiBold text-[7.702px] md:text-[15.4px] font-normal leading-[120%]">
            Q-Commerce
          </span>
          <span className="flex w-[61.618px] md:w-[116.6px] absolute top-36 right-0 md:-right-7 animate-float2 h-[18.155px] md:h-[36.3px] px-[17.6px] py-[8.8px] whitespace-nowrap justify-center items-center shrink-0 rounded-[4px] md:rounded-[8.8px] border border-[#FF6F6F] bg-[#FFEFEF] text-[#BC0000] font-LatinkaSemiBold text-[7.702px] md:text-[15.4px] font-normal leading-[120%]">
            Bus Booking
          </span>
          <Image
            src={
              isMobile
                ? getImageUrl("/vilvabusiness/website/products/vilvaone/busandquick_1-Mobile.webp")
                : getImageUrl("/vilvabusiness/website/products/vilvaone/busandquick_2-Desktop.webp")
            }
            alt="VilvaOne bus booking and Q-Commerce apps preview"
            width={isMobile ? 300 : 500}
            height={isMobile ? 250 : 380}
            className="w-[85%] sm:w-[300px] md:w-[83%] mx-auto"
            priority
          />
        </div>
      </div>
    </div>
  );
};

const DashboardSection = () => {
  const ref = useRef(null);

  return (
    <section ref={ref} className="bg-[#F5F8FF] py-20 md:py-30 px-4 md:px-0">
      <div>
        <p className="md:text-[40px] text-[32px] text-center font-NeueMontreal font-[700] leading-[120%] text-[var(--Dark-Color,#090D28)] mb-2">
          Powerful Dashboards for Businesses
        </p>
        <p className="text-[#373844] text-center font-Schibsted_Grotesk text-[16px] md:text-[18px] font-normal leading-[150%] mb-12">
          Get real-time insights, full operational control, and seamless management - all in one intuitive interface.
        </p>
      </div>

      <div>
        <div className="md:container mb-10 md:mb-20">
          <ProfileCardDashboard
            imageSrc={getImageUrl("/vilvabusiness/website/products/vilvaone/vone_dashboard_2-Desktop.webp")}
            imageSrcMobile={getImageUrl("/vilvabusiness/website/products/vilvaone/vone_dashboard_1-Mobile.webp")}
            imageAlt="VoneDashboard"
          />
        </div>
      </div>

      <div>
        <div className="md:container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <SvgIcons.TicketBookings />, title: "Track Bookings & Orders", text: "View past and upcoming rides or deliveries in one place." },
              { icon: <SvgIcons.ManagePayments />, title: "Manage Payments & Invoices", text: "View receipts, payment history, and refunds." },
              { icon: <SvgIcons.RatingFeedBack />, title: "Ratings & Feedback", text: "Leave reviews, report issues, or contact support." },
              { icon: <SvgIcons.ProfileCustomization />, title: "Profile Customization", text: "Update user details, preferences, and notifications." },
            ].map((item, index) => (
              <div key={index} className="flex justify-center items-center gap-4 flex-col px-1">
                {item.icon}
                <p className="text-[#090D28] text-center font-Schibsted_Grotesk text-[22px] font-bold leading-[120%]">{item.title}</p>
                <p className="text-[#373844] text-center font-Schibsted_Grotesk text-[16px] font-normal md:w-[100%] w-[80%] leading-[150%]">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// FIX: added isMobile to prop signature so it can be used inside
const BusBookingSection = ({ faqData, openIndex, handleToggle, isMobile }: {
  faqData: { question: string; answer: string }[];
  openIndex: number | null;
  handleToggle: (index: number) => void;
  isMobile: boolean;
}) => {
  const ref = useRef(null);

  return (
    <section ref={ref} className="px-4 md:container overflow-hidden">
      <div className="grid items-start grid-cols-1 gap-12 md:grid-cols-1 lg:grid-cols-2">
        <div>
          <h2 className="font-NeueMontreal text-[32px] pt-[80px] md:pt-[200px] md:text-[36px] not-italic font-[700] leading-[120%] bg-gradient-to-r from-[#FF0A0A] via-[#E29E00] to-[#E29E00] bg-clip-text text-transparent mb-2">
            Bus Booking App
          </h2>
          <div className="w-full">
            <div>
              <div className="bg-gradient-to-r from-[#FF0A0A] via-[#C000E200] to-[#fff] h-[2px] w-full" />
            </div>
          </div>
          <div className="mt-[31px]">
            {faqData.map((item, index) => (
              <FaqItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
                isLast={index === faqData.length - 1}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="relative">
            <div className="">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[403px] h-[250px] md:w-[300px] md:h-[300px] shrink-0 rounded-[535px] bg-[#FFD6D6] blur-[100px]"></div>
              <div className="relative w-fit mx-auto">

                <div className="relative w-[580px] h-[350px] mx-auto">
                  <Image
                    src={
                      isMobile
                        ? getImageUrl("/vilvabusiness/website/products/vilvaone/busbooking_mob_1.webp")
                        : getImageUrl("/vilvabusiness/website/products/vilvaone/BusBooking.webp")
                    }
                    alt="VilvaOne bus booking app interface"
                    fill
                    className="object-contain z-20"
                    loading="lazy"
                  />
                </div>
                <div className="flex  flex-col items-start justify-start absolute top-9 sm:top-20 right-3 md:-right-11 lg:-right-0 xl:right-0 gap-[26px]">
                  <Image
                    src={getImageUrl("/vilvabusiness/website/products/vilvaone/bus_mini_img3.webp")}
                    alt="VilvaOne bus booking app seat selection screen"
                    fill
                    className="relative pl-0 sm:pl-4 z-20 w-[180px] sm:w-[250px] md:w-[200px] lg:w-[250px] xl:w-[300px]"
                    loading="lazy"
                    decoding="async"
                  />
                  <Image
                    src={getImageUrl("/vilvabusiness/website/products/vilvaone/bus_mini_img (1)_1.webp")}
                    alt="VilvaOne bus booking app ticket details screen"
                    width={800}
                    height={500}
                    className="relative z-20 pl-0 sm:pl-4   w-[120px] md:w-[150px] lg:w-[150px] xl:w-[200px]"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const QCommerceSection = ({ faqData2, openIndex2, handleToggle2 }: {
  faqData2: { question: string; answer: string }[];
  openIndex2: number | null;
  handleToggle2: (index: number) => void;
}) => {
  const ref = useRef(null);

  return (
    <section ref={ref} className="px-4 md:container overflow-hidden">
      <div className="grid items-start grid-cols-1 gap-12 md:grid-cols-1 lg:grid-cols-2">
        <div>
          <div className="relative pt-0 hidden lg:block  lg:pt-[56px] py-[56px]">
            <div className="">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[203px] h-[250px] md:w-[300px] md:h-[300px] shrink-0 rounded-[535px] bg-[#B7FABD] blur-[100px]"></div>
              <div className="relative w-fit mx-auto">

                <Image
                  src={getImageUrl("/vilvabusiness/website/products/vilvaone/QuickCom.webp")}
                  alt="VilvaOne Q-Commerce delivery app interface"
                  width={800}
                  height={500}
                  className="relative left-[100px] md:left-14 lg:left-[98px] w-full md:w-[350px] mt-10 lg:w-[580px] z-20"
                  loading="lazy"
                  decoding="async"
                />

                <div className="flex  flex-col items-end justify-end absolute top-9 sm:top-20 left-3 md:-left-11 lg:-left-0 xl:left-0 gap-[26px]">
                  <Image
                    src={getImageUrl("/vilvabusiness/website/products/vilvaone/quick_img (1).webp")}
                    alt="VilvaOne Q-Commerce app product catalog screen"
                    width={800}
                    height={500}
                    className="relative z-20 pr-0 sm:pr-4   w-[130px] md:w-[150px] lg:w-[150px] xl:w-[200px]"
                    loading="lazy"
                    decoding="async"
                  />
                  <Image
                    src={getImageUrl("/vilvabusiness/website/products/vilvaone/quick_img (2).webp")}
                    alt="VilvaOne Q-Commerce app order tracking screen"
                    width={800}
                    height={500}
                    className="relative pr-0 sm:pr-4  z-20 w-[180px] sm:w-[250px] md:w-[200px] lg:w-[250px] xl:w-[300px]"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="order-2">
            <h2 className="font-NeueMontreal pt-[0px] lg:pt-[200px] text-[32px] md:text-[36px] not-italic font-[700] leading-[120%] bg-gradient-to-r from-[#05CA0F] via-[#05CA0F] to-[#009032] bg-clip-text text-transparent mb-2">
              Q-Commerce
            </h2>

            <div>
              <div className="bg-gradient-to-r from-[#05CA0F] via-[#C000E200] to-[#fff] h-[2px]" />
            </div>

            <div className="mt-[31px]">
              {faqData2.map((item, index) => (
                <FaqItem
                  key={index}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openIndex2 === index}
                  onToggle={() => handleToggle2(index)}
                  isLast={index === faqData2.length - 1}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          {/* <div className="relative block lg:hidden  pt-0  lg:pt-[56px] py-[56px]">
            <div className="">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[203px] h-[250px] md:w-[300px] md:h-[300px] shrink-0 rounded-[535px] bg-[#B7FABD] blur-[100px]"></div>
              <div className="relative w-fit mx-auto">
                <img src=getImageUrl("/vilvabusiness/qucomMobile.svg") className="relative  left-[100px] md:left-14 lg:left-[98px] w-full md:w-[350px] mt-10 lg:w-[580px] z-20" alt="Main bus booking mobile interface" />
                <div className="flex  flex-col items-end justify-end absolute top-9 sm:top-20 left-3 md:-left-11 lg:-left-0 xl:left-0 gap-[26px]">
                  <img src={getImageUrl("/vilvabusiness/qcommini2.svg")} className="relative z-20 pr-0 sm:pr-4   w-[130px] md:w-[150px] lg:w-[150px] xl:w-[200px]" alt="Main bus booking mobile interface" />
                  <img src={getImageUrl("/vilvabusiness/qcommini1.svg")} className="relative pr-0 sm:pr-4  z-20 w-[180px] sm:w-[250px] md:w-[200px] lg:w-[250px] xl:w-[300px]" alt="Main bus booking mobile interface" />
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};

const WhyTeamsLoveUsSection = () => {
  const ref = useRef(null);

  return (
    <section ref={ref} className="py-[60px] md:py-[100px] px-4 md:px-0 md:container">
      <div >
        <h2 className="text-[#090D28] text-center text-[32px] md:text-[48px] leading-[120%] font-NeueMontreal">Why teams love{" "} <br className="sm:hidden block" />
          <span className="text-[32px] md:text-[48px]  leading-[120%] font-NeueMontreal bg-gradient-to-r from-[#1586FF] to-[#003FDF] bg-clip-text text-transparent"
          >Working With VilvaOne</span> </h2>
        <p className="text-[#010839] text-center text-[16px] md:text-[18px] font-normal leading-[150%] font-Schibsted_Grotesk py-4">
          When our customers win, we win. Here's how they've used our tools to succeed.
        </p>
      </div>

      <div>
        <section className="flex items-center justify-center gap-3">
          <section className="flex items-center justify-center gap-3 ">
            <Image src={getImageUrl("/vilvabusiness/website/common/otherallimages/Frame%20642.png")} width={220} height={220} alt="Customer success stories with VilvaOne" />
          </section>
        </section>
      </div>

      <div>
        <section className="pt-[40px] md:pt-[80px] flex justify-center items-center">
          {/* Old testimonials hidden — unified to CircularTestimonialView */}
          {/* <Testimonials /> */}
          <CircularTestimonialView />
        </section>
      </div>

    </section>
  );
};

export default VilvaOne;